/**
 * B1020 — the storefront Gift cards page (842:2 @1440, 843:2 @390): the pure
 * half. The route, the offer the guest session reads (`giftcard.offer`), the
 * buyer's draft, its refusals in words, the preview's facts, and the body for
 * `cart.gift_card.add`. The card joins the SAME cart the tickets page holds
 * (the tab's session: token + cartId), so tickets and cards pay together.
 * Pure: no React, no I/O; clocks and zones are passed in.
 */
import { formatMoney, type Money } from "./money.js";

/* ------------------------------------------------------------------ route */

export interface GiftCardsRoute {
  readonly tenantId?: string;
  readonly siteKey?: string;
  /** B1020 (2b) — `?edit=<lineId>`: the cart row's EDIT; the form loads that card and saves it in place. */
  readonly edit?: string;
}

/** `/gift-cards?tenant=<id>` (or `?siteKey=`); anything else is not this page. */
export function giftCardsRouteFrom(loc: { readonly pathname: string; readonly search: string }): GiftCardsRoute | undefined {
  if (!/\/gift-cards\/?$/.test(loc.pathname)) return undefined;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(loc.search);
  } catch {
    params = new URLSearchParams();
  }
  const tenantId = params.get("tenant") ?? "";
  const siteKey = params.get("siteKey") ?? "";
  const edit = params.get("edit") ?? "";
  return { ...(tenantId !== "" ? { tenantId } : {}), ...(siteKey !== "" ? { siteKey } : {}), ...(edit !== "" ? { edit } : {}) };
}

/** The tickets page of the same business — the bar's ‹ door and where CHECKOUT goes (the cart lives there). */
export function ticketsHref(tenantId: string): string {
  return `/?tenant=${encodeURIComponent(tenantId)}`; // vocab-ok: a URL query key (B830 tenant addressing), never rendered
}

/** B1020 (d) — 842:2's CHECK A BALANCE →: the public balance page (B1022) for the same business. */
export function balanceHref(tenantId: string): string {
  return `/gift-cards/balance?tenant=${encodeURIComponent(tenantId)}`; // vocab-ok: a URL query key (B830 tenant addressing), never rendered
}

/* ------------------------------------------------------------------ offer */

export interface GiftCardDesign {
  readonly designId: string;
  readonly name: string;
}

export interface GiftCardOffer {
  readonly enabled: boolean;
  readonly currency: string;
  readonly presets: readonly number[];
  readonly customMin: number;
  readonly customMax: number;
  readonly cardsPerOrder: number;
  readonly designs: readonly GiftCardDesign[];
}

/** `giftcard.offer`'s answer, narrowed; anything malformed reads as OFF (the page never sells on a guess). */
export function offerFrom(res: unknown): GiftCardOffer {
  const r = (typeof res === "object" && res !== null ? res : {}) as Record<string, unknown>;
  const int = (v: unknown): number | undefined => (typeof v === "number" && Number.isInteger(v) && v > 0 ? v : undefined);
  const currency = typeof r["currency"] === "string" && /^[A-Z]{3}$/.test(r["currency"]) ? r["currency"] : "USD";
  const presets = Array.isArray(r["presets"]) ? r["presets"].map(int).filter((n): n is number => n !== undefined) : [];
  const customMin = int(r["customMin"]);
  const customMax = int(r["customMax"]);
  const cardsPerOrder = int(r["cardsPerOrder"]) ?? 1;
  const designs = Array.isArray(r["designs"])
    ? r["designs"].flatMap((d) => {
        const o = (typeof d === "object" && d !== null ? d : {}) as Record<string, unknown>;
        return typeof o["designId"] === "string" && typeof o["name"] === "string" ? [{ designId: o["designId"], name: o["name"] }] : [];
      })
    : [];
  const sane = customMin !== undefined && customMax !== undefined && customMin <= customMax;
  return {
    enabled: r["enabled"] === true && sane,
    currency,
    presets: [...presets].sort((a, b) => a - b),
    customMin: customMin ?? 0,
    customMax: customMax ?? 0,
    cardsPerOrder,
    designs,
  };
}

/** Minor units in one major unit of the currency (2 for USD, 0 for JPY). */
export function minorDigits(currency: string): number {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** A whole amount's face, no cents: 5000 USD → "$50". */
export function wholeMoney(amount: number, currency: string): string {
  const digits = minorDigits(currency);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount / 10 ** digits);
  } catch {
    return String(amount / 10 ** digits);
  }
}

const UNIT_WORDS: Readonly<Record<string, string>> = { USD: "dollars", CAD: "dollars", AUD: "dollars", NZD: "dollars", SGD: "dollars", GBP: "pounds", EUR: "euros" };

/** "Whole dollars, $10 to $500." — the custom field's hint. */
export function customHint(offer: GiftCardOffer): string {
  return `Whole ${UNIT_WORDS[offer.currency] ?? "amounts"}, ${wholeMoney(offer.customMin, offer.currency)} to ${wholeMoney(offer.customMax, offer.currency)}.`;
}

/** The AMOUNT card's head note (842:2). */
export function amountNote(offer: GiftCardOffer, businessName: string | undefined): string {
  const whose = businessName !== undefined && businessName.trim() !== "" ? `${businessName.trim()}'s` : "ours";
  return `Presets are ${whose}; a custom amount can be anything from ${wholeMoney(offer.customMin, offer.currency)} to ${wholeMoney(offer.customMax, offer.currency)}.`;
}

/* ------------------------------------------------------------------ draft */

export type AmountPick = { readonly kind: "preset"; readonly amount: number } | { readonly kind: "custom" };

export interface GiftCardDraft {
  readonly designId?: string;
  readonly pick: AmountPick;
  readonly customText: string;
  readonly recipientName: string;
  readonly recipientEmail: string;
  readonly senderName: string;
  readonly send: "now" | "date";
  /** YYYY-MM-DD, the day the email goes out at 9:00. */
  readonly sendDate: string;
  readonly message: string;
  readonly copyToMe: boolean;
}

export const MESSAGE_MAX = 200;
export const NAME_MAX = 120;

/** The form as it opens: the second preset (842:2 picks $50 of $25–$150 — one step up from the least), the first design, NOW. */
export function freshDraft(offer: GiftCardOffer, today: string): GiftCardDraft {
  const mid = offer.presets[Math.min(1, offer.presets.length - 1)];
  return {
    ...(offer.designs[0] !== undefined ? { designId: offer.designs[0].designId } : {}),
    pick: mid !== undefined ? { kind: "preset", amount: mid } : { kind: "custom" },
    customText: "",
    recipientName: "",
    recipientEmail: "",
    senderName: "",
    send: "now",
    sendDate: today,
    message: "",
    copyToMe: true,
  };
}

/** The custom field's whole amount in minor units, or undefined while it reads as no whole amount. */
export function customMinor(text: string, currency: string): number | undefined {
  const t = text.trim().replace(/^[^\d]+/u, "").replace(/,/g, "");
  if (!/^\d{1,7}$/.test(t)) return undefined;
  return Number(t) * 10 ** minorDigits(currency);
}

/** What the card carries, when the draft names an amount at all. */
export function draftAmount(draft: GiftCardDraft, offer: GiftCardOffer): number | undefined {
  return draft.pick.kind === "preset" ? draft.pick.amount : customMinor(draft.customText, offer.currency);
}

export type DraftField = "amount" | "recipientName" | "recipientEmail" | "senderName" | "sendDate" | "message";
export type DraftIssues = Partial<Record<DraftField, string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Every refusal the form owes BEFORE the cart is asked — each beside its own field. */
export function draftIssues(draft: GiftCardDraft, offer: GiftCardOffer, today: string): DraftIssues {
  const out: { -readonly [K in DraftField]?: string } = {};
  if (draft.pick.kind === "custom") {
    const amount = customMinor(draft.customText, offer.currency);
    const range = `${wholeMoney(offer.customMin, offer.currency)} to ${wholeMoney(offer.customMax, offer.currency)}`;
    if (draft.customText.trim() === "") out.amount = `Enter an amount from ${range}, or pick one above.`;
    else if (amount === undefined) out.amount = `Enter a whole amount from ${range} — no cents.`;
    else if (amount < offer.customMin || amount > offer.customMax) out.amount = `A gift card here can be ${range}.`;
  }
  const rn = draft.recipientName.trim();
  if (rn === "") out.recipientName = "Enter their name — it's on the card and the email.";
  else if (rn.length > NAME_MAX) out.recipientName = `Keep their name to ${NAME_MAX} characters.`;
  const re = draft.recipientEmail.trim();
  if (re === "") out.recipientEmail = "Enter their email — the card goes there.";
  else if (!EMAIL.test(re) || re.length > 254) out.recipientEmail = "That doesn't look like an email address — check it and try again.";
  const sn = draft.senderName.trim();
  if (sn === "") out.senderName = "Enter your name — it's shown as the sender.";
  else if (sn.length > NAME_MAX) out.senderName = `Keep your name to ${NAME_MAX} characters.`;
  if (draft.send === "date") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.sendDate)) out.sendDate = "Pick the day it should arrive.";
    else if (draft.sendDate < today) out.sendDate = "Pick today or a later day.";
    else if (draft.sendDate > addDays(today, 365)) out.sendDate = "Pick a day within the next year.";
  }
  if (draft.message.length > MESSAGE_MAX) out.message = `Keep the message to ${MESSAGE_MAX} characters — it's ${draft.message.length} now.`;
  return out;
}

/** YYYY-MM-DD plus n days (calendar arithmetic, no zone). */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Today's YYYY-MM-DD in a zone (the business's clock; the browser's when it has none). */
export function todayIn(now: number, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    return new Date(now).toISOString().slice(0, 10);
  }
}

/** The zone's offset from UTC at an instant, in ms (read from its "GMT-08:00" name — no clock shape of our own, B621). */
function offsetMs(at: number, timeZone: string): number {
  const name = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" }).formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name);
  if (m === null) return 0;
  return (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 3_600_000 + Number(m[3] ?? "0") * 60_000);
}

/** 9:00 on that day in the zone, as an instant (842:2: "Arrives at 9:00"). */
export function nineAm(day: string, timeZone: string | undefined): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d, 9);
  if (timeZone === undefined) return wall;
  try {
    let at = wall - offsetMs(wall, timeZone);
    at = wall - offsetMs(at, timeZone);
    return at;
  } catch {
    return wall;
  }
}

/** "Nov 2, 2026" — the preview's and the date field's register. */
export function dayWords(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }).format(Date.UTC(y, m - 1, d));
}

const firstName = (full: string): string => full.trim().split(/\s+/u)[0] ?? "";

/* ---------------------------------------------------------------- preview */

export interface PreviewFacts {
  readonly amount: string;
  readonly to: string;
  readonly from?: string;
  readonly when: string;
  readonly total: string;
  readonly note: string;
}

/** WHAT <NAME> WILL GET (842:2's right column) — what the recipient's email will show. */
export function previewFacts(draft: GiftCardDraft, offer: GiftCardOffer, today: string): PreviewFacts & { readonly head: string } {
  const amount = draftAmount(draft, offer);
  const who = firstName(draft.recipientName);
  const sender = firstName(draft.senderName);
  const inRange = amount !== undefined && (draft.pick.kind === "preset" || (amount >= offer.customMin && amount <= offer.customMax));
  return {
    head: who !== "" ? `What ${who} will get` : "What they will get",
    amount: inRange ? wholeMoney(amount, offer.currency) : "—",
    to: draft.recipientName.trim() !== "" ? `To ${draft.recipientName.trim()}` : "To —",
    ...(sender !== "" ? { from: `from ${sender}` } : {}),
    when: draft.send === "now" ? "Today, once you pay" : /^\d{4}-\d{2}-\d{2}$/.test(draft.sendDate) && draft.sendDate >= today ? dayWords(draft.sendDate) : "—",
    total: inRange ? formatMoney({ amount, currency: offer.currency }) : "—",
    note: `This is what ${who !== "" ? who : "they"} ${who !== "" ? "gets" : "get"} — the email shows the design, your message, the number and the PIN.`,
  };
}

/* ------------------------------------------------------------------- cart */

/** The ONE door's words: "Add to cart · $50.00". */
export function doorWords(draft: GiftCardDraft, offer: GiftCardOffer): string {
  const amount = draftAmount(draft, offer);
  return amount === undefined ? "Add to cart" : `Add to cart · ${formatMoney({ amount, currency: offer.currency })}`;
}

export interface CartFacts {
  readonly cards: number;
  readonly cardTotal?: Money;
  /** Every line on the cart, tickets and cards — the bar's CART · N. */
  readonly lines: number;
}

/** `cart.get`'s answer, narrowed to what this page says about it. */
export function cartFacts(res: unknown): CartFacts {
  const r = (typeof res === "object" && res !== null ? res : {}) as Record<string, unknown>;
  const cards = Array.isArray(r["giftCards"]) ? (r["giftCards"] as unknown[]) : [];
  const lines = Array.isArray(r["lines"]) ? r["lines"].length : 0;
  let total: Money | undefined;
  for (const c of cards) {
    const a = (c as { amount?: { amount?: unknown; currency?: unknown } }).amount;
    if (typeof a?.amount === "number" && typeof a.currency === "string") total = { amount: (total?.amount ?? 0) + a.amount, currency: a.currency };
  }
  return { cards: cards.length, ...(total !== undefined ? { cardTotal: total } : {}), lines: lines + cards.length };
}

/** Under the door (842:2): "2 gift cards already in your cart · $150.00 — add this one, then add the next person's or go on." */
export function cartNote(cart: CartFacts): string | undefined {
  if (cart.cards === 0) return undefined;
  const n = cart.cards === 1 ? "1 gift card" : `${cart.cards} gift cards`;
  const total = cart.cardTotal !== undefined ? ` · ${formatMoney(cart.cardTotal)}` : "";
  return `${n} already in your cart${total} — add this one, then add the next person's or go on.`;
}

/** The door shut with its reason when the cart already holds the most cards one order can. */
export function cartFullWhy(cart: CartFacts, offer: GiftCardOffer): string | undefined {
  return cart.cards >= offer.cardsPerOrder ? `One order can hold ${offer.cardsPerOrder} gift cards and your cart has them all — check out, then start another order.` : undefined;
}

/** The `cart.gift_card.add` fields for a draft the form has passed. */
export function addBody(draft: GiftCardDraft, offer: GiftCardOffer, cartId: string, timeZone: string | undefined): Record<string, unknown> {
  const amount = draftAmount(draft, offer);
  if (amount === undefined) throw new Error("addBody: a draft without an amount");
  const message = draft.message.trim();
  return {
    cartId,
    amount,
    ...(draft.designId !== undefined ? { designId: draft.designId } : {}),
    recipientName: draft.recipientName.trim(),
    recipientEmail: draft.recipientEmail.trim(),
    senderName: draft.senderName.trim(),
    ...(message !== "" ? { message } : {}),
    ...(draft.send === "date" ? { sendAt: nineAm(draft.sendDate, timeZone) } : {}),
  };
}

/* ------------------------------------------------------------------ edit */

/** The form's door while it edits a card already on the cart: "Save changes · $75.00". */
export function saveWords(draft: GiftCardDraft, offer: GiftCardOffer): string {
  const amount = draftAmount(draft, offer);
  return amount === undefined ? "Save changes" : `Save changes · ${formatMoney({ amount, currency: offer.currency })}`;
}

/**
 * B1020 (2b) — the cart row's EDIT: the card on `lineId`, as `cart.get`
 * answered it, back in the form's own shape. A preset amount picks its tile;
 * any other is CUSTOM in whole units. A send date reads as that day in the
 * business's zone. The copy is what this tab recorded for the line (none
 * recorded = off — the honest reading). A line the cart no longer holds, or
 * a malformed answer, loads nothing: the page falls back to a fresh card.
 */
export function draftFromCard(
  cartRes: unknown,
  lineId: string,
  offer: GiftCardOffer,
  timeZone: string | undefined,
  copies: Readonly<Record<string, boolean>>,
  /** The day a switch to ON A DATE starts from (the business's today); the pure half reads no clock. */
  today = "",
): GiftCardDraft | undefined {
  const cards = typeof cartRes === "object" && cartRes !== null ? (cartRes as { giftCards?: unknown }).giftCards : undefined;
  if (!Array.isArray(cards)) return undefined;
  const card = (cards as unknown[]).find((c) => typeof c === "object" && c !== null && (c as { lineId?: unknown }).lineId === lineId) as Record<string, unknown> | undefined;
  const amount = (card?.["amount"] as { amount?: unknown } | undefined)?.amount;
  if (card === undefined || typeof amount !== "number" || !Number.isInteger(amount) || amount <= 0) return undefined;
  const str = (k: string): string => (typeof card[k] === "string" ? (card[k] as string) : "");
  const sendAt = typeof card["sendAt"] === "number" && Number.isFinite(card["sendAt"]) ? (card["sendAt"] as number) : undefined;
  const unit = 10 ** minorDigits(offer.currency);
  const preset = offer.presets.includes(amount);
  return {
    ...(str("designId") !== "" ? { designId: str("designId") } : {}),
    pick: preset ? { kind: "preset", amount } : { kind: "custom" },
    customText: preset ? "" : String(amount / unit),
    recipientName: str("recipientName"),
    recipientEmail: str("recipientEmail"),
    senderName: str("senderName"),
    send: sendAt !== undefined ? "date" : "now",
    sendDate: sendAt !== undefined ? todayIn(sendAt, timeZone) : today,
    message: str("message"),
    copyToMe: copies[lineId] === true,
  };
}

/** The `cart.gift_card.update` fields: the whole card, as the add sends it, on its own line. */
export function updateBody(draft: GiftCardDraft, offer: GiftCardOffer, cartId: string, lineId: string, timeZone: string | undefined): Record<string, unknown> {
  const { cartId: id, ...card } = addBody(draft, offer, cartId, timeZone);
  return { cartId: id, lineId, ...card };
}

/* ----------------------------------------------------------- copy-to-me */

/**
 * "Email me a copy of the card" — the buyer's email is asked at Cart & pay
 * (855:268: "email for the receipt + copies"), so the choice rides this tab's
 * session per cart line until the pay step reads it.
 */
export const COPY_KEY = "transact.storefront.giftCardCopies";

export function rememberCopy(storage: { getItem(k: string): string | null; setItem(k: string, v: string): void } | null, lineId: string, copy: boolean): void {
  try {
    const prior = JSON.parse(storage?.getItem(COPY_KEY) ?? "{}") as Record<string, boolean>;
    storage?.setItem(COPY_KEY, JSON.stringify({ ...prior, [lineId]: copy }));
  } catch {
    /* a broken store forgets the copy; the receipt still goes */
  }
}

/** What this tab recorded per cart line; a missing or broken store reads as none. */
export function recallCopies(storage: { getItem(k: string): string | null } | null): Readonly<Record<string, boolean>> {
  try {
    const raw = JSON.parse(storage?.getItem(COPY_KEY) ?? "{}") as unknown;
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
    return Object.fromEntries(Object.entries(raw as Record<string, unknown>).filter((e): e is [string, boolean] => typeof e[1] === "boolean"));
  } catch {
    return {};
  }
}
